/**
 * SharedRunningAnalysis — public view of a shared running analysis (read-only).
 * Thin wrapper: fetches shared data and delegates all UI to RunningAnalysisView.
 */

import GarminAttribution from '../../components/garmin/GarminAttribution';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { tokens } from '../../theme';
import { useTranslation } from 'react-i18next';
import { getSharedRunningAnalysis, unshareRunningAnalysis } from '../../api/runningShareApi';
import RunningAnalysisView from '../../components/running/RunningAnalysisView';

import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';

const SharedRunningAnalysis = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { shareId } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Unshare dialog
  const [unshareDialogOpen, setUnshareDialogOpen] = useState(false);
  const [unshareLoading, setUnshareLoading] = useState(false);

  useEffect(() => {
    if (!shareId) return;
    const fetchSharedAnalysis = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getSharedRunningAnalysis(shareId);
        setShareData(data);
      } catch (err) {
        console.error('Failed to load shared analysis:', err);
        setError(err.response?.data?.detail || 'Failed to load shared analysis');
      } finally {
        setLoading(false);
      }
    };
    fetchSharedAnalysis();
  }, [shareId]);

  const handleUnshare = async () => {
    try {
      setUnshareLoading(true);
      await unshareRunningAnalysis(shareId);
      setUnshareDialogOpen(false);
      navigate('/running');
    } catch (err) {
      console.error('Failed to unshare:', err);
      alert('Failed to unshare analysis');
    } finally {
      setUnshareLoading(false);
    }
  };

  if (loading) {
    return (
      <Box m={isMobile ? '10px' : '20px'} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !shareData) {
    return (
      <Box m={isMobile ? '10px' : '20px'}>
        <Alert severity="error">{error || 'Shared analysis not found'}</Alert>
      </Box>
    );
  }

  const headerLeft = (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <ShareIcon sx={{ color: colors.blueAccent[500] }} />
        <Typography variant={isMobile ? 'h4' : 'h2'} fontWeight="bold" color={colors.grey[100]}>
          {shareData.display_name || t('sharedRunningAnalysis', 'Shared Running Analysis')}
        </Typography>
      </Box>
      {/* The device credit follows the data wherever it is shown, including a
          share viewed by someone else. */}
      <GarminAttribution
        source={shareData.workout_data?.file_source}
        deviceName={shareData.workout_data?.device_name}
        sx={{ mb: 1 }}
      />
      {shareData.description && (
        <Typography variant="body1" color={colors.grey[300]} mb={1}>
          {shareData.description}
        </Typography>
      )}
      <Typography variant="body2" color={colors.grey[400]}>
        {shareData.is_anonymous
          ? t('anonymousShare', 'Anonymous share')
          : `Shared by ${shareData.owner_name || 'Unknown'}`}
        {' • '}
        {new Date(shareData.shared_at).toLocaleDateString()}
        {shareData.view_count > 0 && ` • ${shareData.view_count} views`}
      </Typography>
    </Box>
  );

  const headerRight = (
    <>
      {shareData.is_owner && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setUnshareDialogOpen(true)}
          sx={{
            minWidth: isMobile ? 'auto' : undefined,
            px: isMobile ? 2 : undefined,
            fontSize: isMobile ? '0.75rem' : undefined,
          }}
        >
          Unshare
        </Button>
      )}
      <Button
        variant="outlined"
        onClick={() => navigate('/running')}
        sx={{
          color: colors.grey[100],
          borderColor: colors.primary[400],
          minWidth: isMobile ? 'auto' : undefined,
          px: isMobile ? 2 : undefined,
          fontSize: isMobile ? '0.75rem' : undefined,
          '&:hover': { borderColor: colors.primary[300], backgroundColor: colors.primary[700] },
        }}
      >
        {isMobile ? t('runningDashboard', 'Dashboard') : t('backToRunningDashboard', 'Back to Running Dashboard')}
      </Button>
    </>
  );

  return (
    <>
      <RunningAnalysisView
        workoutData={shareData.workout_data}
        headerLeft={headerLeft}
        headerRight={headerRight}
      />

      <Dialog open={unshareDialogOpen} onClose={() => setUnshareDialogOpen(false)}>
        <DialogTitle>Unshare Analysis?</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete the shareable link. The original analysis will remain in your account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnshareDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUnshare}
            color="error"
            disabled={unshareLoading}
            startIcon={unshareLoading && <CircularProgress size={16} />}
          >
            {unshareLoading ? 'Unsharing...' : 'Unshare'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SharedRunningAnalysis;
